import React, { useContext, useEffect, useState } from 'react';
import { Container, Row } from 'shards-react';
import PageTitle from '../../../components/PageTitle';
import GenericSearchBar from '../../../components/common/GenericSearchBar/GenericSearchBar';

import UserEmailInput from '../../../components/inputs/UserEmailInput';
import AccountNameOrIdInput from '../../../components/inputs/AccountNameOrIdInput';
import ShopNameInput from '../../../components/inputs/ShopNameInput';
import EstablishmentNumberInput from '../../../components/inputs/EstablishmentNumberInput';
import CredentialUsernameInput from '../../../components/inputs/CredentialUsernameInput';
import RegisteredInfoProvidersInput from '../../../components/inputs/RegisteredInfoProvidersInput';
import ScrapeTable from './ScrapeTable';

import ScraperComponent from './ScraperComponent';
import SchedulerService from '../../../services/SchedulerService';
import AuthorizedDocumentService from '../../../services/AuthorizedDocumentService';
import Constants from '../../../data/Constants';
import DismissableMessagesList from '../../../components/DismissableMessagesList';
import Errors from '../../../components/Errors';
import { AuthContext } from '../../../contexts/AuthContext';
import { useFullEstablishments } from '../../../hooks/queries/useFullEstablishments';
import { useUrlState } from '../../../hooks/useUrlState';
import { positiveIntegerParam, stringParam } from '../../../utils/urlStateParams';

const inputs = [
  UserEmailInput,
  EstablishmentNumberInput,
  ShopNameInput,
  CredentialUsernameInput,
  AccountNameOrIdInput,
  RegisteredInfoProvidersInput
];

const URL_STATE_CONFIG = {
  userEmail: stringParam(),
  establishmentNumberOrId: stringParam(),
  shopName: stringParam(),
  credentialUsername: stringParam(),
  infoProvider: stringParam(),
  accountNameOrId: stringParam(),
  page: positiveIntegerParam()
};

const SupportDashboard = () => {
  const [urlState, setUrlState] = useUrlState(URL_STATE_CONFIG);
  const [selectedEstablishments, setSelectedEstablishments] = useState([]);
  const [scrapingJobState, setScrapingJobState] = useState([]);
  const [scrapingErrors, setScrapingErrors] = useState([]);
  const { realUser, userEmail } = useContext(AuthContext);

  const offset = (urlState.page - 1) * Constants.elementsPerPage;
  const filter = {
    userEmail: urlState.userEmail || undefined,
    shopName: urlState.shopName || undefined,
    establishmentNumberOrId: urlState.establishmentNumberOrId || undefined,
    credentialInfoProvider: urlState.infoProvider || undefined,
    credentialUsername: urlState.credentialUsername || undefined,
    accountNameOrId: urlState.accountNameOrId || undefined
  };
  const {
    establishments,
    loading: establishmentsLoading,
    error: establishmentsError
  } = useFullEstablishments(filter, {
    limit: Constants.elementsPerPage,
    offset
  });

  const onFilterSubmit = (newFilter) => {
    setSelectedEstablishments([]);
    setUrlState({
      userEmail: newFilter.userEmail?.trim() || '',
      establishmentNumberOrId: newFilter.establishmentNumberOrId?.trim() || '',
      shopName: newFilter.shopName?.trim() || '',
      credentialUsername: newFilter.credentialUsername?.trim() || '',
      infoProvider: newFilter.infoProvider || '',
      accountNameOrId: newFilter.accountNameOrId?.trim() || '',
      page: 1
    });
  };

  const handlePageClick = (direction) => {
    const page = urlState.page + direction;
    if (page >= 1) {
      setSelectedEstablishments([]);
      setUrlState({ page });
    }
  };

  useEffect(() => {
    setSelectedEstablishments([]);
    window.scrollTo(0, 0);
  }, [
    urlState.userEmail,
    urlState.establishmentNumberOrId,
    urlState.shopName,
    urlState.credentialUsername,
    urlState.infoProvider,
    urlState.accountNameOrId,
    urlState.page
  ]);

  const handleSelectEstablishment = (establishment, selected) => {
    setSelectedEstablishments((current) => {
      if (selected) {
        return current.some((item) => item.id === establishment.id)
          ? current
          : [...current, establishment];
      }
      return current.filter((item) => item.id !== establishment.id);
    });
  };

  const onSelectAll = () => setSelectedEstablishments(establishments);
  const onUnselectAll = () => setSelectedEstablishments([]);

  const groupEstablishmentsPayload = (query) => {
    const credentialIds = new Set(
      selectedEstablishments.flatMap((establishment) =>
        establishment.credentials.map((credential) => credential.id)
      )
    );
    const payloads = [...credentialIds].map((credentialId) => ({
      establishmentNumbers: [],
      credentialId,
      documentTypes: [query.documentType],
      startDate: query.dateFrom,
      endDate: query.dateTo,
      originDetail: realUser ? realUser.email : userEmail
    }));

    selectedEstablishments.forEach((establishment) => {
      establishment.credentials.forEach((credential) => {
        const targetPayload = payloads.find((payload) => payload.credentialId === credential.id);
        if (targetPayload) targetPayload.establishmentNumbers.push(establishment.number);
      });
    });

    return payloads;
  };

  const dismissMessage = (message) => {
    setScrapingJobState((current) => current.filter((item) => item !== message));
  };

  const sendToScheduler = async (docTypes, params) => {
    if (!docTypes.map((docType) => docType.documentType).includes(params.documentTypes[0])) return;

    const schedulerService = await SchedulerService();
    const data = await schedulerService.scheduleScrapping(params);
    if (data.proposeScrappingJob) {
      const message = `UUID: ${data.proposeScrappingJob.requestUuid} - CredentialID: ${data.proposeScrappingJob.credentialId}`;
      setScrapingJobState((current) =>
        current.includes(message) ? current : [...current, message]
      );
    } else {
      setScrapingErrors((current) => [
        ...current,
        data.message || 'No se pudo iniciar el scraping.'
      ]);
    }
  };

  const onSendToScrap = async (query) => {
    setScrapingJobState([]);
    setScrapingErrors([]);

    try {
      if (
        query.dateFrom &&
        query.dateTo &&
        query.documentType &&
        selectedEstablishments.length > 0
      ) {
        const payloads = groupEstablishmentsPayload(query);
        const authorizedDocumentService = await AuthorizedDocumentService();
        await Promise.all(
          payloads.map(async (params) => {
            const docTypes = await authorizedDocumentService.getAuthDocs(params.credentialId);
            await sendToScheduler(docTypes, params);
          })
        );
      }
    } catch {
      setScrapingErrors((current) => [...current, 'No se pudo completar el scraping.']);
    }
  };

  const errors = [
    ...(establishmentsError ? ['No se pudieron cargar los establecimientos.'] : []),
    ...scrapingErrors
  ];

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Soporte" />
      </Row>

      <GenericSearchBar handleSubmit={onFilterSubmit} inputs={inputs} values={urlState} />
      <ScraperComponent
        onSelectAll={onSelectAll}
        onSendToScrap={onSendToScrap}
        onUnselectAll={onUnselectAll}
      />
      <DismissableMessagesList dismissMessage={dismissMessage} messages={scrapingJobState} />
      <Errors errors={errors.length ? errors : undefined} />
      <ScrapeTable
        disableNext={establishments.length < Constants.elementsPerPage}
        disablePrev={urlState.page === 1}
        establishments={establishments}
        loading={establishmentsLoading}
        onPageClick={handlePageClick}
        onSelect={handleSelectEstablishment}
        selectedEstablishments={selectedEstablishments}
      />
    </Container>
  );
};

export default SupportDashboard;
